import { useState, useEffect, useRef } from 'react';
import { User, Search, ChevronDown, X, Plus } from 'lucide-react';
import { useContactsQuery } from '../../hooks/useQueries';
import useModalBack from '../../hooks/useModalBack';

const AVATAR_COLORS = [
  'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-blue-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500'
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export default function ContactSelect({
  value,
  displayValue,
  onChange,
  placeholder = 'Search or select contact',
  error,
  onAddContact,
  autoOpen = false
}) {
  const [isOpen, setIsOpen] = useState(autoOpen);
  useModalBack(isOpen, () => setIsOpen(false), 'ContactSelect');
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Open when autoOpen prop changes to true (e.g. "Change" button clicked)
  useEffect(() => {
    if (autoOpen) setIsOpen(true);
  }, [autoOpen]);

  // Clear search when dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  // Fetch contacts with search
  const { data: contactsData = {}, isLoading } = useContactsQuery(
    { search: search || undefined, limit: 10000 },
    { enabled: true }
  );

  const contacts = contactsData.contacts || [];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1280;

  // Close dropdown when clicking outside (desktop only — mobile uses backdrop overlay)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);


  const handleSelect = (contact) => {
    onChange(contact);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ _id: '', name: '' });
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative h-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-colors text-left ${
          error
            ? 'border-red-500'
            : isOpen
            ? 'border-primary-500 ring-2 ring-primary-100'
            : 'border-surface-200 hover:border-surface-300'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {value && displayValue ? (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold ${getAvatarColor(displayValue)}`}>
              {getInitials(displayValue)}
            </div>
          ) : (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-surface-100">
              <User className="w-3.5 h-3.5 text-surface-400" />
            </div>
          )}
          <span className={`text-sm truncate ${value ? 'text-surface-900' : 'text-surface-400'}`}>
            {displayValue || placeholder}
          </span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-surface-100 rounded"
            >
              <X className="w-3.5 h-3.5 text-surface-400" />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-surface-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Mobile: Bottom Sheet */}
      {isOpen && isMobile && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]" onClick={() => setIsOpen(false)} />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] animate-slide-up flex flex-col" style={{ maxHeight: '80vh' }}>
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
              <div className="w-9 h-1 bg-surface-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 flex-shrink-0">
              <h3 className="text-lg font-extrabold text-surface-900">Select Contact</h3>
              <button type="button" onClick={() => setIsOpen(false)} className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center">
                <X className="w-4 h-4 text-surface-500" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 pb-3 flex-shrink-0">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-surface-100 rounded-xl">
                <Search className="w-4 h-4 text-surface-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search contacts..."
                  className="flex-1 bg-transparent text-sm outline-none text-surface-900 placeholder:text-surface-400"
                />
              </div>
            </div>

            {/* Contact List + Add New Contact */}
            <div className="overflow-y-auto px-3 flex-1 min-h-0 pb-6">
              {isLoading ? (
                <div className="py-8 text-center text-sm text-surface-500">Loading...</div>
              ) : contacts.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-surface-500 mb-3">{search ? 'No contacts found' : 'No contacts yet'}</p>
                  {onAddContact && (
                    <button
                      type="button"
                      onClick={() => { setIsOpen(false); onAddContact(); }}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
                      style={{ backgroundColor: 'var(--accent-bg)' }}
                    >
                      <Plus className="w-4 h-4" />
                      Add New Contact
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {contacts.map((contact) => (
                    <button
                      key={contact._id}
                      type="button"
                      onClick={() => handleSelect(contact)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        value === contact._id ? 'bg-primary-50' : 'active:bg-surface-50'
                      }`}
                    >
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${getAvatarColor(contact.name)}`}>
                        {getInitials(contact.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[15px] font-semibold truncate ${value === contact._id ? 'text-primary-700' : 'text-surface-900'}`}>
                          {contact.name}
                        </p>
                        {(contact.email || contact.mobile) && (
                          <p className="text-xs text-surface-400 truncate">{contact.mobile || contact.email}</p>
                        )}
                      </div>
                    </button>
                  ))}

                  {/* Add New Contact — inside scroll area, dashed outline style like mockup */}
                  {onAddContact && (
                    <div className="mt-3 px-1 pb-2">
                      <button
                        type="button"
                        onClick={() => { setIsOpen(false); onAddContact(); }}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-colors"
                        style={{ borderColor: 'var(--accent-light)', backgroundColor: 'color-mix(in srgb, var(--accent-light) 30%, white)', color: 'var(--accent-bg)' }}
                      >
                        <Plus className="w-4 h-4" />
                        Add New Contact
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop: Dropdown */}
      {isOpen && !isMobile && (
        <div className="absolute z-[70] mt-1 w-full bg-white border border-surface-200 rounded-xl shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-1.5 border-b border-surface-100">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs">🔍</span>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-surface-200 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          {/* Contacts List */}
          <div className="max-h-36 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-center text-sm text-surface-500">
                Loading...
              </div>
            ) : contacts.length === 0 ? (
              <div className="p-3 text-center text-sm text-surface-500">
                {search ? 'No contacts found' : 'No contacts yet'}
              </div>
            ) : (
              <div className="py-0.5">
                {contacts.map((contact) => (
                  <button
                    key={contact._id}
                    type="button"
                    onClick={() => handleSelect(contact)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-50 transition-colors ${
                      value === contact._id ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold ${getAvatarColor(contact.name)}`}>
                      {getInitials(contact.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${
                        value === contact._id ? 'text-primary-700 font-medium' : 'text-surface-900'
                      }`}>
                        {contact.name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
